import React from 'react';
import type { ContentBlock } from '@/pages/blog/editor';
import Image from 'next/image';

// Centralized style classes for each block type
const blockStyles = {
  heading: 'mt-8 mb-4 font-bold text-gray-900 dark:text-gray-100',
  paragraph: 'mb-4 text-gray-800 dark:text-gray-100',
  list: 'mb-4 list-inside text-gray-800 dark:text-gray-100',
  image: 'my-8 rounded-lg shadow-md dark:shadow-gray-700',
  video: 'my-8 rounded-lg shadow-md w-full dark:shadow-gray-700',
  quote: 'border-l-4 border-gray-200 dark:border-gray-700 pl-4 my-8 italic',
  quoteText: 'text-xl text-gray-800 dark:text-gray-100',
  quoteAuthor: 'text-gray-600 mt-2 dark:text-gray-400',
  code: 'bg-gray-100 p-4 rounded-lg my-8 overflow-x-auto dark:bg-gray-800',
  divider: 'my-8 border-gray-300 dark:border-gray-700',
  tag: 'px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-100 rounded-full text-sm',
};

export interface BlogContentRendererProps {
  blocks: ContentBlock[];
  onBlockClick?: (blockId: string) => void;
  selectedBlockId?: string | null;
  getBlockId?: (block: ContentBlock, index: number) => string | undefined;
}

// Helper function to parse markdown-style links
function parseLinks(text: string) {
  const parts = [];
  let lastIndex = 0;
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Add the link
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : text;
}

export default function BlogContentRenderer({ blocks, onBlockClick, selectedBlockId, getBlockId }: BlogContentRendererProps) {
  return (
    <div className="prose max-w-none dark:prose-invert">
      {blocks.map((block, i) => {
        // For editor preview: wrap in clickable/highlightable div if onBlockClick is provided
        const blockId = getBlockId ? getBlockId(block, i) : undefined;
        const isSelected = selectedBlockId && blockId && selectedBlockId === blockId;
        const blockNode = (() => {
          switch (block.type) {
            case 'heading':
              return React.createElement(
                `h${block.level}`,
                { className: blockStyles.heading },
                parseLinks(block.text)
              );
            case 'paragraph':
              return <p className={blockStyles.paragraph}>{parseLinks(block.text)}</p>;
            case 'list':
              return block.ordered ? (
                <ol className={blockStyles.list + ' list-decimal'}>
                  {block.items.map((item, idx) => <li key={idx}>{parseLinks(item)}</li>)}
                </ol>
              ) : (
                <ul className={blockStyles.list + ' list-disc'}>
                  {block.items.map((item, idx) => <li key={idx}>{parseLinks(item)}</li>)}
                </ul>
              );
            case 'image':
              const alt = block.alt || 'Image';
              return <Image src={block.src} alt={alt} width={block.width} height={block.height} className={blockStyles.image} />;
            case 'video':
              return <video src={block.src} autoPlay={block.autoPlay} muted={block.muted} loop={block.loop} playsInline={block.playsInline} controls={block.controls} className={blockStyles.video} />;
            case 'quote':
              return (
                <blockquote className={blockStyles.quote}>
                  <p className={blockStyles.quoteText}>{parseLinks(block.text)}</p>
                  {block.author && <footer className={blockStyles.quoteAuthor}>— {block.author}</footer>}
                </blockquote>
              );
            case 'code':
              return <pre className={blockStyles.code}><code className={`language-${block.language} text-gray-800 dark:text-gray-100`}>{block.code}</code></pre>;
            case 'divider':
              return <hr className={blockStyles.divider} />;
            default:
              return null;
          }
        })();
        if (onBlockClick && blockId) {
          return (
            <div
              key={blockId}
              data-block-id={blockId}
              onClick={() => onBlockClick(blockId)}
              className={`cursor-pointer transition-all duration-200 ${
                isSelected
                  ? 'ring-1 ring-blue-500 ring-offset-1 rounded-sm'
                  : 'hover:ring-1 hover:ring-gray-300 hover:ring-offset-1 hover:rounded-sm'
              }`}
            >
              {blockNode}
            </div>
          );
        } else {
          return <React.Fragment key={blockId || i}>{blockNode}</React.Fragment>;
        }
      })}
    </div>
  );
} 